import { useQueries } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  fetchRoleById,
  PermissionsQueryKeys,
} from "@/api/roles-permissions/roles-permissions.queries";
import { fetchRolesIn } from "@/api/roles-permissions/rolesIn";
import { SCOPE_BOX_LABEL } from "@/api/roles-permissions/roleScope";
import type { UserScopeCompany } from "@/api/access/userScope.types";
import type { ScopeRule } from "@/components/features/RolesPermissions/SetScopeModal/scopeRules";
import {
  blockingRules,
  companyForNewUser,
  givenAsEntityCopies,
  reconcileRules,
  rolesRequiring,
  SUPER_ADMIN_ROLE_ID,
  type RoleScopeSource,
} from "@/components/features/user-management/add-user/newUserScope";
import { requiredBoxMessage } from "@/components/features/user-management/add-user/newUserCopy";
import type { DropDownOption } from "@/types/common.types";

/**
 * UI26: the roles of the platform company and of each picked entity, which New User reads at the
 * ServEngine level to give an entity-level pick as each entity's own copy. The same reads, under
 * the same keys, as Add a Role's (`useGivableRoles`).
 */
export function useServEngineRoleCopies(entityIds: string[], enabled: boolean) {
  const places: Array<string | null> = enabled ? [null, ...entityIds] : [];
  const results = useQueries({
    queries: places.map((entityId) => ({
      queryKey: [PermissionsQueryKeys.getAllRoles, "in", entityId ?? "servengine"],
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchRolesIn(entityId, signal),
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const servEngineRoles = results[0]?.data ?? [];
  const rolesByEntity = Object.fromEntries(
    entityIds.map((entityId, index) => [entityId, results[index + 1]?.data ?? []]),
  );
  return {
    isReady: results.every((result) => result.isSuccess),
    resolve: (roleId: string) => givenAsEntityCopies(roleId, servEngineRoles, rolesByEntity),
  };
}

export interface UseNewUserScopeArgs {
  enabled: boolean;
  entityIds: string[];
  entityOptions: DropDownOption[];
  assignedRoleIds: string[];
  temporaryRoleIds: string[];
}

/**
 * Step 2 of New User: one card per picked entity, holding the rule rows the person's roles ask
 * for. A role's required boxes are read from the role itself, since the person does not exist yet.
 */
export function useNewUserScope({
  enabled,
  entityIds,
  entityOptions,
  assignedRoleIds,
  temporaryRoleIds,
}: UseNewUserScopeArgs) {
  const [rulesByEntity, setRulesByEntity] = useState<Record<string, ScopeRule[]>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [blockedAttempt, setBlockedAttempt] = useState(0);

  const roleIds = useMemo(
    () =>
      enabled
        ? Array.from(
            new Set([
              ...assignedRoleIds.filter((id) => id !== SUPER_ADMIN_ROLE_ID),
              ...temporaryRoleIds.filter(Boolean),
            ]),
          )
        : [],
    [enabled, assignedRoleIds, temporaryRoleIds],
  );

  const roleQueries = useQueries({
    queries: roleIds.map((roleId) => ({
      queryKey: ["newUserRoleScope", roleId],
      queryFn: () => fetchRoleById(roleId, { suppressErrorAlert: true }),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const roleSources: RoleScopeSource[] = roleQueries.flatMap((query) => {
    const role = query.data?.data;
    return role?.scopeKinds ? [{ roleName: role.name, scopeKinds: role.scopeKinds }] : [];
  });
  const roleSourcesKey = JSON.stringify(roleSources);
  const rolesStillLoading = roleQueries.some((query) => query.isLoading);
  const unreadRoleCount = roleQueries.filter((query) => query.isError).length;

  const companies = useMemo<UserScopeCompany[]>(() => {
    const sources = JSON.parse(roleSourcesKey) as RoleScopeSource[];
    return entityIds.map((tenantId) =>
      companyForNewUser(
        tenantId,
        entityOptions.find((option) => option.value === tenantId)?.label ?? tenantId,
        sources,
      ),
    );
  }, [entityIds, entityOptions, roleSourcesKey]);
  const companiesKey = JSON.stringify(companies);

  // Re-seeded only when the entities or what their roles ask for change: reconciling mints row
  // keys, and doing it on every render would remount every open picker (stage 8 found this).
  useEffect(() => {
    if (!enabled) return;
    const current = JSON.parse(companiesKey) as UserScopeCompany[];
    setRulesByEntity((previous) =>
      Object.fromEntries(
        current.map((company) => [
          company.tenantId,
          reconcileRules(company, previous[company.tenantId] ?? []),
        ]),
      ),
    );
  }, [companiesKey, enabled]);

  const blockedTenantIds = useMemo(
    () =>
      new Set(
        companies
          .filter((company) => blockingRules(rulesByEntity[company.tenantId] ?? []).length > 0)
          .map((company) => company.tenantId),
      ),
    [companies, rulesByEntity],
  );

  // Errors are for the attempt that found them: once every row is answered they go, so a row
  // added afterwards is not shown red before the user has had a chance to fill it in.
  useEffect(() => {
    if (showErrors && blockedTenantIds.size === 0) setShowErrors(false);
  }, [showErrors, blockedTenantIds]);

  /** Shows what blocks Create User; true when nothing does. */
  const checkBeforeCreate = () => {
    setShowErrors(true);
    if (blockedTenantIds.size === 0) return true;
    setBlockedAttempt((attempt) => attempt + 1);
    return false;
  };

  const requiredError = (company: UserScopeCompany, rule: ScopeRule) =>
    rule.box
      ? requiredBoxMessage(
          rolesRequiring(roleSources, rule.box),
          SCOPE_BOX_LABEL[rule.box],
          company.tenantName,
        )
      : undefined;

  const roleNameOf = (roleId: string) =>
    roleQueries[roleIds.indexOf(roleId)]?.data?.data?.name ?? roleId;

  const setEntityRules = (tenantId: string, rules: ScopeRule[]) =>
    setRulesByEntity((previous) => ({ ...previous, [tenantId]: rules }));

  return {
    companies,
    rulesByEntity,
    setEntityRules,
    showErrors,
    blockedAttempt,
    blockedTenantIds,
    checkBeforeCreate,
    requiredError,
    roleNameOf,
    rolesStillLoading,
    unreadRoleCount,
  };
}
